import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Container
} from '@mui/material';
import {
  Add,
  Email,
  Phone,
  Person,
  Schedule,
  MoreVert,
  ArrowBack,
  Edit,
  Delete,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const SupplierCommunications = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCommunication, setSelectedCommunication] = useState(null);

  useEffect(() => {
    fetchCommunications();
  }, [id]);

  const fetchCommunications = async () => {
    try {
      const response = await suppliersAPI.getCommunications(id);
      setCommunications(response.data.data.communications);
    } catch (error) {
      enqueueSnackbar('Error fetching communications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await suppliersAPI.addCommunication(id, {
        type: 'note',
        content: newNote,
        date: new Date().toISOString(),
      });
      enqueueSnackbar('Note added successfully', { variant: 'success' });
      setNewNote('');
      fetchCommunications();
    } catch (error) {
      enqueueSnackbar('Error adding note', { variant: 'error' });
    }
  };

  const handleMenuOpen = (event, communication) => {
    setAnchorEl(event.currentTarget);
    setSelectedCommunication(communication);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCommunication(null);
  };

  const handleDelete = async () => {
    if (!selectedCommunication) return;

    try {
      await suppliersAPI.deleteCommunication(id, selectedCommunication._id);
      enqueueSnackbar('Communication deleted successfully', { variant: 'success' });
      handleMenuClose();
      fetchCommunications();
    } catch (error) {
      enqueueSnackbar('Error deleting communication', { variant: 'error' });
    }
  };

  const getCommunicationIcon = (type) => {
    switch (type) {
      case 'email':
        return <Email />;
      case 'phone':
        return <Phone />;
      case 'meeting':
        return <Person />;
      default:
        return <Email />;
    }
  };

  const getCommunicationColor = (type) => {
    switch (type) {
      case 'email':
        return 'primary';
      case 'phone':
        return 'success';
      case 'meeting':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Back button and header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/pharmacy/suppliers')}
            variant="outlined"
          >
            Back to Suppliers
          </Button>
          <Typography variant="h3" fontWeight="bold">
            Supplier Communications
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Add New Note */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Add New Note
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a note about your communication..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    startIcon={<Add />}
                  >
                    Add Note
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Communications List */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Communication History
                </Typography>

                {communications.length === 0 ? (
                  <Alert severity="info">
                    No communications recorded yet.
                  </Alert>
                ) : (
                  <List>
                    {communications.map((communication, index) => (
                      <React.Fragment key={communication._id}>
                        <ListItem alignItems="flex-start" secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={(e) => handleMenuOpen(e, communication)}
                          >
                            <MoreVert />
                          </IconButton>
                        }>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: `${getCommunicationColor(communication.type)}.main` }}>
                              {getCommunicationIcon(communication.type)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  {communication.subject || 'Note'}
                                </Typography>
                                <Chip
                                  label={communication.type}
                                  size="small"
                                  color={getCommunicationColor(communication.type)}
                                />
                                {communication.followUpRequired && (
                                  <Chip
                                    label="Follow Up"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="h6" color="text.primary" paragraph>
                                  {communication.content}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Schedule fontSize="small" color="action" />
                                  <Typography variant="body1" color="text.secondary">
                                    {new Date(communication.date).toLocaleString()}
                                  </Typography>
                                  {communication.createdBy && (
                                    <Typography variant="body1" color="text.secondary">
                                      By {communication.createdBy}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < communications.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuClose}>
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <Delete sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
};